import { MODAL_Z_INDEX } from '@constants/z-indices';
import { useSectionMutations } from '@ContractBuilder/hooks/useSectionMutations';
import { useSectionModal } from '@ContractBuilder/modules/navigation';
import { getSectionLabel } from '@ContractBuilder/modules/navigation/modules/section-modal/utils/get-section-label';
import { SectionForm } from '@ContractBuilder/modules/navigation/modules/section-modal/views/SectionForm';
import { useEntityStore } from '@ContractBuilder/store';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { useForm } from '@hooks/form';
import { useDeepCompareMemo } from '@hooks/use-deep-compare-memo';
import { useLanguage } from '@root/src/language';
import clsx from 'clsx';

import { sectionSchema } from '../schema';
import type { SectionModalFormState } from '../types';
import { getInitialValues } from '../utils/get-initial-state';

export const SectionModalController = () => {
  'use memo';

  const { submission } = useEntityStore(({ submission }) => ({ submission }));
  const { updateSection, createSection } = useSectionMutations();

  const { getContent } = useLanguage({ prefix: 'naming.mrc.modals.editSection' });

  const { activeSectionId, isSectionModalOpen, closeSectionModal } = useSectionModal();
  const isEditing = activeSectionId !== undefined;
  const modalMode = isEditing ? 'edit' : 'create';

  const handleFormSubmit = async (formValues: SectionModalFormState) => {
    if (!submission) {
      return;
    }

    const section = submission.sections.find((item) => item.id === activeSectionId);
    const { name, standardisedSectionName, shouldHideTitleInPdf, shouldResetPageCounter } = formValues;

    const payload = {
      label: getSectionLabel(name, standardisedSectionName) as string,
      standardised_name: standardisedSectionName,
      should_hide_title_in_pdf: shouldHideTitleInPdf,
      should_reset_page_counter: shouldResetPageCounter,
    };

    if (section && isEditing) {
      await updateSection.mutateAsync({ sectionId: activeSectionId, payload });
    } else {
      await createSection.mutateAsync(payload);
    }

    return closeSectionModal();
  };

  const initialState = useDeepCompareMemo(
    () => getInitialValues(submission, activeSectionId),
    [submission, activeSectionId],
  );

  const { getFieldProps, onSubmit, isLoading } = useForm<SectionModalFormState>({
    initialState,
    onSubmit: handleFormSubmit,
    validationSchema: sectionSchema,
    shouldEnableReinitialization: true,
  });

  const handleSubmit = async () => {
    await onSubmit();
  };

  return (
    <Dialog
      open={isSectionModalOpen}
      as="div"
      className={clsx('fixed inset-0 overflow-y-auto', MODAL_Z_INDEX)}
      data-testid="section-modal"
      onClose={closeSectionModal}
    >
      <div className="flex min-h-screen min-w-96 items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="bg-info-500/75 fixed inset-0 transition-opacity" />

        <DialogPanel
          transition
          className="flex h-screen items-center justify-center duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
        >
          <div className="relative inline-block transform rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
            <div className="absolute top-0 right-0 hidden pt-7 pr-4 sm:block">
              <button
                type="button"
                className="text-info-400 hover:text-info-500 rounded-md bg-white focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:outline-hidden"
                onClick={closeSectionModal}
              >
                <span className="sr-only">Cancel</span>
                <XIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0">
                <DialogTitle as="h3" className="text-info-900! mb-6 text-2xl! leading-6 font-bold!">
                  {getContent(`title.${modalMode}`)}
                </DialogTitle>
                <SectionForm
                  getFieldProps={getFieldProps}
                  modalMode={modalMode}
                  isLoading={isLoading}
                  onClose={closeSectionModal}
                  onSubmit={handleSubmit}
                />
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
